import type { DbAsset } from '@/api/astraea';

interface PhotoCompareProps {
  referenceUrl?: string | null;
  previewUrl: string;
  referenceLabel?: string;
  previewLabel?: string;
}

export function PhotoCompare({ referenceUrl, previewUrl, referenceLabel = '原形象', previewLabel = '换装预览' }: PhotoCompareProps) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-1">
        <div className="text-[10px] text-secondary-500 font-medium">{referenceLabel}</div>
        {referenceUrl ? (
          <img src={referenceUrl} alt={referenceLabel} className="w-full rounded-lg aspect-[3/4] object-cover border border-secondary-200" />
        ) : (
          <div className="w-full rounded-lg aspect-[3/4] bg-secondary-100 border border-dashed border-secondary-300 flex items-center justify-center text-[10px] text-secondary-400">无参考图</div>
        )}
      </div>
      <div className="space-y-1">
        <div className="text-[10px] text-primary-600 font-medium">{previewLabel}</div>
        <img src={previewUrl} alt={previewLabel} className="w-full rounded-lg aspect-[3/4] object-cover border-2 border-primary-300" />
      </div>
    </div>
  );
}

interface PendingAssetCardProps {
  asset: DbAsset;
  onConfirm: (id: number) => void;
  onReject: (id: number) => void;
  busy?: boolean;
}

export function PendingAssetCard({ asset, onConfirm, onReject, busy }: PendingAssetCardProps) {
  const mode = (asset.meta?.mode as string) || asset.asset_type;
  return (
    <div className="rounded-lg border-2 border-amber-200 bg-amber-50/50 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold">
          <i className="ri-time-line mr-1"></i>待你确认入库
        </span>
        <span className="text-[10px] text-secondary-500">{mode}</span>
      </div>
      {asset.asset_type === 'image' && asset.url && (
        <img src={asset.url} alt="preview" className="w-full max-w-xs rounded-md aspect-[3/4] object-cover" />
      )}
      {asset.asset_type === 'video' && asset.url && (
        <video src={asset.url} controls className="w-full max-w-xs rounded-md aspect-[9/16] object-contain bg-black" />
      )}
      {asset.asset_type === 'audio' && asset.url && (
        <audio src={asset.url} controls className="w-full" />
      )}
      <p className="text-[11px] text-foreground-600 leading-relaxed">
        满意再点「同意存入档案库」;不满意可放弃,不会进入你的正式素材库。
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => onConfirm(asset.id)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer disabled:opacity-50"
        >
          <i className="ri-check-line"></i>同意存入档案库
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onReject(asset.id)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-medium border border-secondary-300 text-secondary-600 hover:bg-background-100 cursor-pointer disabled:opacity-50"
        >
          <i className="ri-close-line"></i>放弃这张
        </button>
      </div>
    </div>
  );
}

interface WardrobePreviewProps {
  referenceUrl: string;
  previewUrl: string;
  prompt: string;
  busy?: boolean;
  onConfirm: () => void;
  onReject: () => void;
}

export function WardrobePreview({ referenceUrl, previewUrl, prompt, busy, onConfirm, onReject }: WardrobePreviewProps) {
  return (
    <div className="rounded-lg border-2 border-primary-200 bg-primary-50/40 p-3 space-y-3">
      <div className="text-[12px] font-semibold text-foreground-900">
        <i className="ri-eye-line text-primary-500 mr-1"></i>请确认:脸是否还是同一个角色?
      </div>
      <PhotoCompare referenceUrl={referenceUrl} previewUrl={previewUrl} />
      <p className="text-[11px] text-foreground-600 leading-relaxed">
        对比左右两张:右边换装图必须<b>保留同一张脸</b>,只是衣服/场景不同。你觉得相似且满意,再存入角色相册(永久保存)。
      </p>
      {prompt && <p className="text-[10px] text-secondary-500">描述:{prompt}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy} onClick={onConfirm} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer disabled:opacity-50">
          {busy ? <><div className="w-3.5 h-3.5 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin"></div>存入中…</> : <><i className="ri-check-double-line"></i>脸一样,同意存入角色相册</>}
        </button>
        <button type="button" disabled={busy} onClick={onReject} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium border border-secondary-300 text-secondary-600 hover:bg-background-100 cursor-pointer disabled:opacity-50">
          <i className="ri-close-line"></i>不满意,放弃
        </button>
      </div>
    </div>
  );
}

interface CharacterPhotoGridProps {
  photos: DbAsset[];
  currentAvatarUrl?: string | null;
  onSetAvatar: (url: string) => void;
  onDownload: (url: string, name: string) => void;
  setAvatarBusy?: boolean;
}

export function CharacterPhotoGrid({ photos, currentAvatarUrl, onSetAvatar, onDownload, setAvatarBusy }: CharacterPhotoGridProps) {
  if (photos.length === 0) {
    return <div className="text-[11px] text-secondary-400 py-4 text-center">相册还是空的。生成真人形象或模特换衣,确认满意后会永久存在这里。</div>;
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
      {photos.map((p) => {
        const isCurrent = !!currentAvatarUrl && p.url === currentAvatarUrl;
        const mode = (p.meta?.mode as string) || 'photo';
        return (
          <div key={p.id} className={`rounded-lg bg-background-100 border p-1.5 ${isCurrent ? 'border-primary-400 ring-2 ring-primary-200' : 'border-secondary-200'}`}>
            {p.url && <img src={p.url} alt="" className="w-full rounded-md aspect-[3/4] object-cover" />}
            <div className="mt-1 flex items-center justify-between gap-1">
              <span className="text-[9px] text-secondary-500 truncate">{mode === 'wardrobe' ? '换装' : mode === 'avatar' ? '形象' : mode}</span>
              {isCurrent && <span className="text-[9px] text-primary-600 font-medium">当前头像</span>}
            </div>
            <div className="mt-1 flex gap-1">
              {!isCurrent && p.url && (
                <button type="button" disabled={setAvatarBusy} onClick={() => onSetAvatar(p.url as string)} className="flex-1 text-[9px] py-1 rounded bg-primary-50 text-primary-700 hover:bg-primary-100 cursor-pointer disabled:opacity-50">
                  设为头像
                </button>
              )}
              {p.url && (
                <button type="button" onClick={() => onDownload(p.url as string, `photo_${p.id}.png`)} className="text-[9px] py-1 px-1.5 rounded border border-secondary-200 text-secondary-500 hover:text-foreground-700 cursor-pointer">
                  <i className="ri-download-line"></i>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
